import { Award } from 'lucide-react';

/**
 * Compact getpica-style "Statistics" dashboard mockup.
 * Simpler than a full dashboard — sits inside a "Medí el éxito" section on each service page.
 *
 * config = {
 *   accent: {
 *     chartFrom, chartVia, chartTo,           // tailwind gradient classes for bars
 *     kpi1Text, kpi2Text, kpi3Text,           // e.g. 'text-pink-600'
 *     dayPillBg,                              // active tab bg class, e.g. 'bg-violet-500'
 *   },
 *   browserUrl,                                // dashboard.momentia.shop / xxx
 *   period,                                    // 'Últimos 3 días · en vivo'
 *   kpis: [ { icon, label, value, sub, color }, { ... }, { ... } ]
 *   chart: { title, bars: number[13] }
 *   sponsorRow: { title, subtitle, items: [{ icon, label, value }, ...] }
 *   badges: [ { emoji, text, pos: 'tl'|'tr'|'bl'|'br'|'ml'|'mr' } ]
 * }
 */
export default function StatsDashboard({ config }) {
  const {
    accent,
    browserUrl,
    period,
    kpis,
    chart,
    sponsorRow,
    badges,
  } = config;

  const posClass = {
    tl: '-top-3 -left-3',
    tr: '-top-3 -right-3',
    bl: '-bottom-3 -left-3',
    br: '-bottom-3 -right-3',
    ml: 'top-1/2 -left-4 -translate-y-1/2',
    mr: 'top-1/2 -right-4 -translate-y-1/2',
    bml: '-bottom-3 left-8',
    tmr: 'top-1/3 -right-4',
  };

  return (
    <div className="relative w-full">
      <div className="rounded-2xl bg-white text-black p-4 shadow-2xl">
        {/* Fake browser bar */}
        <div className="flex items-center gap-1 mb-3">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <div className="ml-3 text-[9px] text-black/50 truncate">{browserUrl}</div>
        </div>

        {/* Statistics header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="font-display font-bold text-black">Statistics</div>
          <div className="ml-auto text-[9px] text-black/50">{period}</div>
        </div>

        {/* 3 KPIs */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {kpis.map((k) => (
            <div key={k.label} className="p-2 rounded-lg border border-black/8">
              <div className={`text-[9px] font-semibold ${k.color}`}>{k.icon} {k.label}</div>
              <div className="text-lg font-display font-bold text-black">{k.value}</div>
              <div className="text-[9px] text-black/50">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="p-3 rounded-lg border border-black/8 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold text-black">{chart.title}</div>
            <div className="flex items-center gap-1">
              <div className={`px-1.5 py-0.5 rounded ${accent.dayPillBg} text-white text-[8px] font-semibold`}>Día</div>
              <div className="px-1.5 py-0.5 rounded text-black/50 text-[8px]">Semana</div>
              <div className="px-1.5 py-0.5 rounded text-black/50 text-[8px]">Mes</div>
            </div>
          </div>
          <div className="flex items-end justify-between gap-1 h-16">
            {chart.bars.map((h, i) => (
              <div
                key={i}
                className={`flex-1 rounded-t bg-gradient-to-t ${accent.chartFrom} ${accent.chartVia || ''} ${accent.chartTo}`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>

        {/* Sponsor / secondary row */}
        {sponsorRow && (
          <div className="p-3 rounded-lg border border-black/8 bg-gradient-to-br from-amber-50 to-orange-50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-semibold text-black flex items-center gap-1">
                <Award className="w-3 h-3 text-amber-600" /> {sponsorRow.title}
              </div>
              <div className="text-[9px] text-black/50 truncate max-w-[50%]">{sponsorRow.subtitle}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {sponsorRow.items.map((it) => (
                <div key={it.label}>
                  <div className="text-[9px] text-black/50">{it.icon} {it.label}</div>
                  <div className="text-sm font-display font-bold text-black">{it.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Floating badges */}
      {badges.map((b) => (
        <div
          key={b.text}
          className={`absolute ${posClass[b.pos] || posClass.tl} px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1 whitespace-nowrap`}
        >
          {b.emoji} <span>{b.text}</span>
        </div>
      ))}
    </div>
  );
}
